import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { getSyncLogs, searchSyncLogs, getDateRange, SyncLog, PaginatedResponse } from '../services/syncService';
import './LogsPage.css';

const LogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(50);
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [minDate, setMinDate] = useState<Date>(new Date());
  const [maxDate, setMaxDate] = useState<Date>(new Date());
  const hasFetchedDateRange = useRef(false);
  const isFetchingLogs = useRef(false);

  // Fetch date range from API
  useEffect(() => {
    if (hasFetchedDateRange.current) return;
    
    const fetchDateRange = async () => {
      try {
        hasFetchedDateRange.current = true;
        const dateRangeData = await getDateRange();
        setMinDate(moment(dateRangeData.minDate).toDate());
        setMaxDate(moment(dateRangeData.maxDate).toDate());
      } catch (error) {
        console.error('Error fetching date range:', error);
        hasFetchedDateRange.current = false; // Reset on error to allow retry
      }
    };

    fetchDateRange();
  }, []);

  // Fetch logs from API
  useEffect(() => {
    if (isFetchingLogs.current) return;
    
    const fetchLogs = async () => {
      try {
        isFetchingLogs.current = true;
        setLoading(true);
        setError(null);
        
        if (dateRange[0] || dateRange[1]) {
          // Use searchSyncLogs for date range filtering with pagination
          let startDateString: string | undefined;
          let endDateString: string | undefined;

          if (dateRange[0]) {
            startDateString = moment(dateRange[0]).format('YYYY-MM-DD');
          }

          if (dateRange[1]) {
            endDateString = moment(dateRange[1]).format('YYYY-MM-DD');
          }
          
          const response: PaginatedResponse<SyncLog> = await searchSyncLogs(startDateString, endDateString, currentPage, itemsPerPage);
          setLogs(response.data);
          setTotalCount(response.total);
          setTotalPages(response.totalPages);
          setIsFiltered(true);
        } else {
          // Use regular getSyncLogs for pagination
          const response: PaginatedResponse<SyncLog> = await getSyncLogs(currentPage, itemsPerPage);
          setLogs(response.data);
          setTotalCount(response.total);
          setTotalPages(response.totalPages);
          setIsFiltered(false);
        }
      } catch (err) {
        console.error('Error fetching logs:', err);
        setError('Failed to load sync logs. Please try again.');
      } finally {
        setLoading(false);
        isFetchingLogs.current = false;
      }
    };

    fetchLogs();
  }, [currentPage, itemsPerPage, dateRange]);

  const formatDateSafely = (dateString: string) => {
    try {
      const date = moment(dateString);
      if (!date.isValid()) {
        return dateString; // Return original string if invalid
      }
      return date.format('YYYY-MM-DD HH:mm:ss');
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (limit: number) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleDetails = (logId: string) => {
    navigate(`/sync-logs-info/${logId}`);
  };

  const handleClearDateRange = () => {
    setDateRange([null, null]);
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="pagination-btn"
      >
        ← Previous
      </button>
    );

    // First page
    if (startPage > 1) {
      buttons.push(
        <button
          key="1"
          onClick={() => handlePageChange(1)}
          className="pagination-btn"
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="pagination-btn"
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="pagination-btn"
      >
        Next →
      </button>
    );

    return buttons;
  };

  if (loading) {
    return (
      <div className="logs-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading sync logs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="logs-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="logs-page">
      <div className="page-header">
        <h1>Sync Logs</h1>
        <p>Monitor and analyze sync activity</p>
      </div>

      <div className="logs-controls">
        <div className="date-filter">
          <div className="date-input-group">
            <label htmlFor="dateRange">Date Range:</label>
            <div className="date-picker-container">
              <DatePicker
                selectsRange={true}
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onChange={(update) => setDateRange(update)}
                className="date-picker"
                minDate={minDate}
                maxDate={maxDate}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select date range"
                isClearable={false}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
              {(dateRange[0] || dateRange[1]) && (
                <button
                  onClick={handleClearDateRange}
                  className="clear-date-btn"
                  title="Clear date range"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="items-per-page">
          <label htmlFor="itemsPerPage">Show:</label>
          <select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="items-per-page-select"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>
      </div>

      <div className="logs-table-container">
        <table className="logs-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Date</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Unrecognized</th>
              <th>Skipped</th>
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id} className="log-row">
                <td className="log-id">{log.id}</td>
                <td className="log-date">{formatDateSafely(log.created_at)}</td>
                <td className="log-created">{log.created}</td>
                <td className="log-updated">{log.updated}</td>
                <td className="log-unrecognized">{log.unrecognized}</td>
                <td className="log-skipped">{log.skipped}</td>
                <td className="log-total">{log.total}</td>
                <td className="log-actions">
                  <button
                    onClick={() => handleDetails(log.id)}
                    className="get-info-btn"
                  >
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination-info">
          <p>
            Showing {logs.length} of {totalCount} logs
            {isFiltered ? ` (filtered by date range)` : ''}
          </p>
        </div>
        
        {totalPages > 1 && (
          <div className="pagination-controls">
            {renderPaginationButtons()}
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsPage; 